import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/common/PageHeader/PageHeader';
import ArchivePopup from '../../../components/Popup/ArchivePopup/ArchivePopup';
import EditIcon from '../../../components/Table/EditIcon/EditIcon';
import DetailIcon from '../../../components/Table/DetailIcon/DetailIcon';
import Filter from '../../../components/common/Filter/Filter';
import ArchiveIcon from '../../../components/Table/ArchiveIcon/ArchiveIcon';
import ExportLabel from '../../../components/common/ExportLabel/Export';
import AddButton from '../../../components/common/AddButton/AddButton';
import SimpleSearchBox from '../../../components/common/Search/SimpleSearch';
import Sort from '../../../components/common/Sort/Sort';
import '../../../components/common/SubNavbar.css';
import '../../../components/common/CommonText.css';
import '../../../components/common/Table.css';
import './Countries.css';
import { baseurl } from '../../../api';
import DetailPopup from '../../../components/Popup/DetailPopup/DetailPopup';
import { useCheckboxClick } from '../../../checkboxclick';
import ArchiveSelectedItem from '../../../components/common/ArchiveSelectedItemLabel/ArchiveSelectedItems';
import authenticatedAxios from '../../../authenticatedAxios';
import useFilterStore from '../../../store/useFilterStore';
import DeleteIcon from '../../../components/Table/DeleteIcon/DeleteIcon';
import DeletePopup from '../../../components/Popup/DeletePopup/DeletePopup';
import useRefreshStore from '../../../store/useRefreshStore';
import { SuccessAlert } from '../../../components/common/AlertBox/GreenAlert';
import { ArchiveAlert } from '../../../components/common/AlertBox/ArchiveAlert';

const pageKey = 'country';

const Countries = () => {
  const { setFilters, getFilters } = useFilterStore();
  const savedFilters = getFilters(pageKey);
  const [isArchivePopupOpen, setIsArchivePopupOpen] = useState(false);
  const [isDetailPopupOpen, setIsDetailPopupOpen] = useState(false);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [items, setItems] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const sortOptions = ['A-Z', 'Z-A', 'Newest', 'Oldest'];
  const filterOptions = ['Default', 'Archived', 'Unarchived'];
  const [searchParams, setSearchParams] = useState(savedFilters?.searchParams || { term: '' });
  const [selectedFilterOption, setSelectedFilterOption] = useState(savedFilters?.selectedFilterOption || 'Unarchived');
  const [selectedSortOption, setSelectedSortOption] = useState(savedFilters?.selectedSortOption || 'Newest');

  const { isParentSelected, selectedItems, handleParentCheckboxChange, handleChildCheckboxChange, resetCheckboxState } = useCheckboxClick(items);
  const navigate = useNavigate();
  const { refreshKeys, triggerRefresh } = useRefreshStore();
  const refreshKey = refreshKeys['Countries'] || 0;

  const mapSortOptionToQuery = (option) => {
    switch (option) {
      case 'A-Z':
        return 'a_to_z';
      case 'Z-A':
        return 'z_to_a';
      case 'Newest':
        return 'newest';
      case 'Oldest':
        return 'oldest';
      default:
        return 'newest';
    }
  };

  const fetchCountries = async () => {
    try {
      const params = {
        search: searchParams.term,
        filter: selectedFilterOption === 'Default' ? undefined : selectedFilterOption.toLowerCase(),
        sort: mapSortOptionToQuery(selectedSortOption),
      };

      const response = await authenticatedAxios.get(`${baseurl}localization/country/`, { params });
      if (response.data.response_code === 1) {
        setItems(response.data.data);
      } else {
        console.error('Error fetching countries:', response.data.message);
      }
    } catch (error) {
      if (error?.response?.data?.error_code === 1001) {
        navigate('/permission-denied');
      } else {
        console.error('Error fetching countries:', error);
      }
    }
  };

  const handleCountryClick = (countryId) => {
    navigate('/states', { state: { countryId } });
  };

  useEffect(() => {
    fetchCountries();
    setFilters(pageKey, {
      searchParams,
      selectedFilterOption,
      selectedSortOption,
    });
  }, [searchParams, selectedSortOption, selectedFilterOption]);

  useEffect(() => {
    fetchCountries();
  }, [refreshKey]);

  const fetchPlanDetails = async (planId) => {
    try {
      const response = await authenticatedAxios.get(`${baseurl}plans/?id=${planId}`);
      if (response && response.data.response_code === 1) {
        setSelectedPlan(response.data.data);
        setIsDetailPopupOpen(true);
      } else {
        console.error('Error fetching plan details');
      }
    } catch (error) {
      console.error('Error fetching plan details:', error);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await authenticatedAxios.delete(
        `${baseurl}localization/country/?id=${selectedItem.id}`
      );

      if (response.status >= 200 && response.status < 300) {
        setIsDeletePopupOpen(false);
        setSelectedItem(null);
        SuccessAlert('Country deleted successfully');
        fetchCountries();
        triggerRefresh('Countries');
        return;
      }
      throw new Error(`Unexpected status code: ${response.status}`);
    } catch (error) {
      console.error('Error deleting country:', error);
    }
  };

  const handleArchiveSelectedItems = async () => {
    try {
      const response = await authenticatedAxios.post(`${baseurl}localization/country/archive/`, {
        id_list: selectedItems,
      });

      if (response.status >= 200 && response.status < 300) {
        fetchCountries();
        resetCheckboxState();
        setIsArchivePopupOpen(false);
        ArchiveAlert('Selected countries archive status updated successfully', 'success');
      } else {
        console.error('Failed to archive/unarchive selected items.');
      }
    } catch (error) {
      console.error('Error archiving/unarchiving selected items:', error);
    }
  };

  const handleArchive = async () => {
    try {
      const response = await authenticatedAxios.get(
        `${baseurl}localization/country/archive/?id=${selectedItem.id}`
      );

      if (response.status >= 200 && response.status < 300) {
        await fetchCountries();
        setIsArchivePopupOpen(false);
        ArchiveAlert(
          `Country ${selectedItem.is_archived ? 'unarchived' : 'archived'} successfully`,
          'success'
        );
        setSelectedItem(null);
      }
    } catch (error) {
      console.error('Error archiving country:', error);
    }
  };

  const handleSearchSelect = (searchTerm) => {
    setSearchParams({ term: searchTerm });
  };

  const handleSortOptionSelect = (option) => {
    setSelectedSortOption(option);
  };

  const handleFilterSelect = (option) => {
    setSelectedFilterOption(option);
  };

  const handleEditClick = (item) => {
    navigate(`/countries/edit/${item.id}`);
  };

  const handleDetailClick = (item) => {
    navigate(`/countries/detail/${item.id}`);
  };

  const handleDeleteClick = async (item) => {
    setSelectedItem(item);
    setIsDeletePopupOpen(true);
  };

  const handleArchiveClick = async (item) => {
    setSelectedItem(item);
    setIsArchivePopupOpen(true);
  };

  const closePopups = () => {
    setIsArchivePopupOpen(false);
    setIsDetailPopupOpen(false);
    setIsDeletePopupOpen(false);
    setSelectedItem(null);
    setSelectedPlan(null);
  };

  const deletePopupFields = [
    { label: 'Are you sure you want to delete this record? Once deleted, it cannot be recovered.', type: 'text' },
  ];

  const detailPopupFields = selectedPlan
    ? [
        { label: 'Plan Name', type: 'large', value: selectedPlan.plan_name || 'N/A' },
        { label: 'Display Name', type: 'small', value: selectedPlan.display_name || 'N/A' },
        { label: 'Trial Period (Days)', type: 'small', value: selectedPlan.trial_period || 'N/A' },
        { label: 'Monthly Price', type: 'small', value: `${selectedPlan.currency?.symbol || ''} ${selectedPlan.monthly_price || 'N/A'}` },
        { label: 'Yearly Price', type: 'small', value: `${selectedPlan.currency?.symbol || ''} ${selectedPlan.yearly_price || 'N/A'}` },
        {
          label: 'Features',
          type: 'features-list',
          value: selectedPlan.features?.map((feature) => ({
            label: feature.name,
            id: feature.id,
          })) || [],
        },
      ]
    : [];

  const handleExportComplete = () => {
    resetCheckboxState();
  };

  return (
    <div className="whole-container">
      <div className="top-heading">
        <PageHeader pageName=" Localization /" nestedpageName="Countries" linkTo="" />
        <div className="export-container">
          <ArchiveSelectedItem
            selectedItems={selectedItems}
            items={items}
            onArchiveSelected={handleArchiveSelectedItems}
          />
          <ExportLabel
            exportUrl={`${baseurl}localization/country/export/`}
            selectedIds={selectedItems}
            onExportComplete={handleExportComplete}
          />
        </div>
      </div>

      <div className="pagename-container">
        <div className="page-name">
          <h1 className="common-text fts-22 ftw-6 blue">Countries</h1>
        </div>
        <div className="pagename-right-container">
          <AddButton onClick={() => navigate('/countries/create')} />
          <SimpleSearchBox placeholder="Search Country" onSearchSelect={handleSearchSelect} defaultValue={searchParams?.term} />
          <Filter options={filterOptions} onFilterSelect={handleFilterSelect} defaultValue={selectedFilterOption} />
          <Sort options={sortOptions} onOptionSelect={handleSortOptionSelect} defaultValue={selectedSortOption} />
        </div>
      </div>

      <div className="table-container">
        <table className="common-table">
          <thead className="common-table-head">
            <tr className="common-table-thead-tr">
              <th>
                <input
                  className="table-selectbox table-parent-selectbox"
                  type="checkbox"
                  checked={isParentSelected}
                  onChange={handleParentCheckboxChange}
                />
                COUNTRY
              </th>
              <th>ISO 3166 CODE</th>
              <th>CURRENCY</th>
              <th>Timezone</th>
              <th>ACTIVE PLANS</th>
              <th className="for-hide">Actions</th>
            </tr>
          </thead>
          <tbody className="common-table-tbody">
            {items.length > 0 ? (
              items.map((country) => (
                <tr key={country.id} className={country.is_archived ? 'archived-item' : ''}>
                  <td className="country-label">
                    <input
                      className="table-selectbox table-child-selectbox"
                      type="checkbox"
                      checked={selectedItems.includes(country.id)}
                      onChange={() => handleChildCheckboxChange(country.id)}
                    />
                    {country.country_flag && typeof country.country_flag === 'string' ? (
                      <img
                        onClick={() => handleCountryClick(country.id)}
                        src={`${baseurl.replace(/\/$/, '')}${country.country_flag.startsWith('/') ? '' : '/'}${country.country_flag}`}
                        alt={`${country.name} icon`}
                        className="country-icon"
                      />
                    ) : (
                      <span
                        onClick={() => handleCountryClick(country.id)}
                        className="country-icon"
                        style={{ display: 'inline-block' }}
                      />
                    )}

                    <span onClick={() => handleCountryClick(country.id)} className="blue cr-pointer">
                      {country.name}
                    </span>
                  </td>
                  <td>{country?.iso_code}</td>
                  <td>
                    {country?.currency?.name}
                    {country?.currency?.symbol ? ` (${country.currency.symbol})` : ''}
                  </td>

                  <td>
                    {Array.isArray(country.timezones) && country.timezones.length > 0 ? (
                      <>
                        {country.timezones.slice(0, 1).map((tzObj, index) => (
                          <span key={tzObj.id || index}>
                            {tzObj.name} ({tzObj.timezone})
                            {index < Math.min(1, country.timezones.length - 1) && ', '}
                          </span>
                        ))}

                        {country.timezones.length > 1 && (
                          <span className="ml-1 blue">+{country.timezones.length - 1} more</span>
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>

                  <td className="blue cr-pointer">
                    <span className="plan-name">
                      {(country.plans || []).map((plan, index) => (
                        <span key={plan.id} className="plan-name" onClick={() => fetchPlanDetails(plan.id)}>
                          {plan?.plan_name || plan?.display_name}
                          {index < country.plans.length - 1 && <span className="slash lt-gray fts-20"> | </span>}
                        </span>
                      ))}
                    </span>
                  </td>

                  <td className="actions common-action">
                    <DetailIcon onClick={() => handleDetailClick(country)} />
                    <EditIcon onClick={() => handleEditClick(country)} />
                    <ArchiveIcon onClick={() => handleArchiveClick(country)} isArchived={country.is_archived} />
                    <DeleteIcon onClick={() => handleDeleteClick(country)} />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                  No countries available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ArchivePopup
        isOpen={isArchivePopupOpen}
        isArchived={selectedItem?.is_archived}
        archivename={selectedItem?.name}
        onSave={handleArchive}
        onClose={() => setIsArchivePopupOpen(false)}
      />

      <DetailPopup
        isOpen={isDetailPopupOpen}
        title="Plan Details"
        details={detailPopupFields}
        onClose={closePopups}
      />

      <DeletePopup
        isOpen={isDeletePopupOpen}
        title="Delete Country"
        fields={deletePopupFields}
        onSave={handleDelete}
        onClose={closePopups}
      />
    </div>
  );
};

export default Countries;
